import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faGraduationCap,
  faClipboardList,
  faBuilding,
  faChartLine,
  faUserCheck,
  faUserClock,
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faPauseCircle,
  faArrowRight,
  faCalendarAlt,
  faEnvelope,
  faPhone,
  faSpinner,
  faShoppingCart,
  faClock,
  faPlayCircle,
  faBan,
  faEdit,
  faFileAlt
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const DashboardCoordenador = () => {
  const { user, hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [extensaoSelecionada, setExtensaoSelecionada] = useState(null);
  const [detalhesExtensao, setDetalhesExtensao] = useState(null);
  const [loadingDetalhes, setLoadingDetalhes] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/dashboard-coordenador');
      setData(response.data.data);
      
      // Se houver extensões, selecionar a primeira por padrão
      if (response.data.data.extensoes && response.data.data.extensoes.length > 0) {
        setExtensaoSelecionada(response.data.data.extensoes[0].id);
      }
    } catch (err) {
      console.error('Erro ao carregar dashboard:', err);
      setError(err.response?.data?.error || 'Erro ao carregar dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadDetalhesExtensao = async (extensaoId) => {
    try {
      setLoadingDetalhes(true);
      const response = await api.get(`/dashboard-coordenador/resumo-extensao/${extensaoId}`);
      setDetalhesExtensao(response.data.data);
    } catch (err) {
      console.error('Erro ao carregar detalhes:', err);
    } finally {
      setLoadingDetalhes(false);
    }
  };

  useEffect(() => {
    if (extensaoSelecionada) {
      loadDetalhesExtensao(extensaoSelecionada);
    }
  }, [extensaoSelecionada]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
  };

  const getStatusColor = (status) => {
    const colors = {
      'Ativa': 'bg-green-100 text-green-800',
      'Concluida': 'bg-blue-100 text-blue-800',
      'Trancada': 'bg-yellow-100 text-yellow-800',
      'Cancelada': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Ativa': faCheckCircle,
      'Concluida': faGraduationCap,
      'Trancada': faPauseCircle,
      'Cancelada': faTimesCircle
    };
    return icons[status] || faClipboardList;
  };

  const getPedidoStatusColor = (status) => {
    const colors = {
      'Pendente': 'bg-yellow-100 text-yellow-800',
      'Em Andamento': 'bg-blue-100 text-blue-800',
      'Concluído': 'bg-green-100 text-green-800',
      'Cancelado': 'bg-red-100 text-red-800',
      'Aguardando Pagamento': 'bg-orange-100 text-orange-800',
      'Pago': 'bg-emerald-100 text-emerald-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="text-4xl text-indigo-600 animate-spin mb-4" />
          <p className="text-gray-600">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-red-500 mb-4" />
        <h3 className="text-lg font-semibold text-red-800 mb-2">Erro ao carregar dashboard</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={loadDashboard}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  if (!data || !data.extensoes || data.extensoes.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-yellow-500 mb-4" />
        <h3 className="text-lg font-semibold text-yellow-800 mb-2">Nenhuma extensão atribuída</h3>
        <p className="text-yellow-600">
          Você ainda não possui acesso a nenhuma extensão. Entre em contato com o administrador.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">
          Olá, {user?.nome?.split(' ')[0]}! 👋
        </h1>
        <p className="text-indigo-100">
          Bem-vindo ao seu painel de coordenação. Aqui você encontra um resumo das suas extensões.
        </p>
        <div className="mt-4 flex items-center gap-2 text-indigo-100">
          <FontAwesomeIcon icon={faBuilding} />
          <span>Você coordena {data.extensoes.length} extensão(ões)</span>
        </div>
      </div>

      {/* Cards de Estatísticas Gerais */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total de Alunos</p>
              <p className="text-3xl font-bold text-gray-900">{data.estatisticas?.total_alunos || 0}</p>
            </div>
            <div className="bg-blue-100 rounded-full p-3">
              <FontAwesomeIcon icon={faUsers} className="text-2xl text-blue-600" />
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            <span className="text-green-600 font-medium">{data.estatisticas?.alunos_ativos || 0}</span> ativos
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Matrículas</p>
              <p className="text-3xl font-bold text-gray-900">{data.estatisticas?.total_matriculas || 0}</p>
            </div>
            <div className="bg-green-100 rounded-full p-3">
              <FontAwesomeIcon icon={faClipboardList} className="text-2xl text-green-600" />
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            <span className="text-green-600 font-medium">{data.estatisticas?.matriculas_ativas || 0}</span> ativas
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Cursos Ativos</p>
              <p className="text-3xl font-bold text-gray-900">{data.estatisticas?.total_cursos || 0}</p>
            </div>
            <div className="bg-purple-100 rounded-full p-3">
              <FontAwesomeIcon icon={faGraduationCap} className="text-2xl text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Extensões</p>
              <p className="text-3xl font-bold text-gray-900">{data.extensoes?.length || 0}</p>
            </div>
            <div className="bg-orange-100 rounded-full p-3">
              <FontAwesomeIcon icon={faBuilding} className="text-2xl text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Cards de Pedidos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total de Pedidos</p>
              <p className="text-3xl font-bold text-gray-900">{data.pedidos_stats?.total_pedidos || 0}</p>
            </div>
            <div className="bg-blue-100 rounded-full p-3">
              <FontAwesomeIcon icon={faShoppingCart} className="text-2xl text-blue-600" />
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            <span className="text-blue-600 font-medium">{data.pedidos_stats?.pedidos_mes || 0}</span> nos últimos 30 dias
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pedidos Pendentes</p>
              <p className="text-3xl font-bold text-yellow-600">{data.pedidos_stats?.pedidos_pendentes || 0}</p>
            </div>
            <div className="bg-yellow-100 rounded-full p-3">
              <FontAwesomeIcon icon={faClock} className="text-2xl text-yellow-600" />
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Aguardando processamento
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Em Andamento</p>
              <p className="text-3xl font-bold text-blue-600">{data.pedidos_stats?.pedidos_andamento || 0}</p>
            </div>
            <div className="bg-blue-100 rounded-full p-3">
              <FontAwesomeIcon icon={faPlayCircle} className="text-2xl text-blue-600" />
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Sendo processados
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Concluídos</p>
              <p className="text-3xl font-bold text-green-600">{data.pedidos_stats?.pedidos_concluidos || 0}</p>
            </div>
            <div className="bg-green-100 rounded-full p-3">
              <FontAwesomeIcon icon={faCheckCircle} className="text-2xl text-green-600" />
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Finalizados com sucesso
          </div>
        </div>
      </div>

      {/* Atalhos Rápidos */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Atalhos Rápidos</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {hasPermission('academico.notas.lancamento_individual.create') && (
            <Link 
              to="/academico/notas"
              className="flex flex-col items-center p-4 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
            >
              <FontAwesomeIcon icon={faEdit} className="text-3xl text-indigo-600 mb-2" />
              <span className="text-sm font-medium text-indigo-700">Lançar Notas</span>
            </Link>
          )}
          {hasPermission('matriculas.list') && (
            <Link 
              to="/matriculas"
              className="flex flex-col items-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
            >
              <FontAwesomeIcon icon={faClipboardList} className="text-3xl text-green-600 mb-2" />
              <span className="text-sm font-medium text-green-700">Matrículas</span>
            </Link>
          )}
          {hasPermission('alunos.list') && (
            <Link 
              to="/alunos"
              className="flex flex-col items-center p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
            >
              <FontAwesomeIcon icon={faUsers} className="text-3xl text-purple-600 mb-2" />
              <span className="text-sm font-medium text-purple-700">Alunos</span>
            </Link>
          )}
          {hasPermission('pedidos.list') && (
            <Link 
              to="/pedidos"
              className="flex flex-col items-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <FontAwesomeIcon icon={faShoppingCart} className="text-3xl text-blue-600 mb-2" />
              <span className="text-sm font-medium text-blue-700">Pedidos</span>
            </Link>
          )}
        </div>
      </div>

      {/* Seletor de Extensão e Detalhes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sidebar - Lista de Extensões */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <h3 className="font-semibold text-gray-900">Suas Extensões</h3>
          </div>
          <div className="divide-y">
            {data.alunos_por_extensao?.map((ext) => (
              <button
                key={ext.extensao_id}
                onClick={() => setExtensaoSelecionada(ext.extensao_id)}
                className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                  extensaoSelecionada === ext.extensao_id ? 'bg-indigo-50 border-l-4 border-indigo-600' : ''
                }`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-gray-900">{ext.extensao_nome}</p>
                    <p className="text-sm text-gray-500">
                      {ext.total_alunos} alunos • {ext.alunos_ativos} ativos
                    </p>
                  </div>
                  <FontAwesomeIcon icon={faArrowRight} className="text-gray-400" />
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Detalhes da Extensão Selecionada */}
        <div className="lg:col-span-2 space-y-6">
          {loadingDetalhes ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <FontAwesomeIcon icon={faSpinner} className="text-2xl text-indigo-600 animate-spin" />
              <p className="mt-2 text-gray-600">Carregando detalhes...</p>
            </div>
          ) : detalhesExtensao ? (
            <>
              {/* Info da Extensão */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="font-semibold text-lg text-gray-900 mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon={faBuilding} className="text-indigo-600" />
                  {detalhesExtensao.extensao?.nome}
                </h3>
                
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-gray-900">{detalhesExtensao.estatisticas?.total_alunos || 0}</p>
                    <p className="text-xs text-gray-500">Total Alunos</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">{detalhesExtensao.estatisticas?.matriculas_ativas || 0}</p>
                    <p className="text-xs text-gray-500">Matrículas Ativas</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-600">{detalhesExtensao.estatisticas?.matriculas_concluidas || 0}</p>
                    <p className="text-xs text-gray-500">Concluídas</p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-yellow-600">{detalhesExtensao.estatisticas?.matriculas_trancadas || 0}</p>
                    <p className="text-xs text-gray-500">Trancadas</p>
                  </div>
                </div>
              </div>

              {/* Cursos com Matrículas */}
              {detalhesExtensao.matriculas_por_curso && detalhesExtensao.matriculas_por_curso.length > 0 && (
                <div className="bg-white rounded-lg shadow">
                  <div className="p-4 border-b">
                    <h3 className="font-semibold text-gray-900">Cursos nesta Extensão</h3>
                  </div>
                  <div className="divide-y">
                    {detalhesExtensao.matriculas_por_curso.map((curso) => (
                      <div key={curso.idCurso} className="p-4 flex justify-between items-center">
                        <div>
                          <p className="font-medium text-gray-900">{curso.curso_nome}</p>
                          <p className="text-sm text-gray-500">
                            {curso.total} matrículas no total
                          </p>
                        </div>
                        <div className="text-right">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {curso.ativas} ativas
                          </span>
                          {curso.concluidas > 0 && (
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {curso.concluidas} concluídas
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Últimos Alunos */}
              {detalhesExtensao.ultimos_alunos && detalhesExtensao.ultimos_alunos.length > 0 && (
                <div className="bg-white rounded-lg shadow">
                  <div className="p-4 border-b flex justify-between items-center">
                    <h3 className="font-semibold text-gray-900">Últimos Alunos Cadastrados</h3>
                    {hasPermission('alunos.list') && (
                      <Link 
                        to="/alunos" 
                        className="text-sm text-indigo-600 hover:text-indigo-800"
                      >
                        Ver todos →
                      </Link>
                    )}
                  </div>
                  <div className="divide-y">
                    {detalhesExtensao.ultimos_alunos.slice(0, 5).map((aluno) => (
                      <div key={aluno.idPessoa} className="p-4 flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <div className="bg-gray-100 rounded-full w-10 h-10 flex items-center justify-center">
                            <FontAwesomeIcon icon={faUsers} className="text-gray-500" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{aluno.nome}</p>
                            <div className="flex items-center gap-3 text-sm text-gray-500">
                              {aluno.email && (
                                <span className="flex items-center gap-1">
                                  <FontAwesomeIcon icon={faEnvelope} className="text-xs" />
                                  {aluno.email}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            aluno.matriculas_ativas > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {aluno.matriculas_ativas > 0 ? `${aluno.matriculas_ativas} matrícula(s)` : 'Sem matrícula'}
                          </span>
                          <p className="text-xs text-gray-400 mt-1">
                            <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                            {formatDate(aluno.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              Selecione uma extensão para ver os detalhes
            </div>
          )}
        </div>
      </div>

      {/* Matrículas Recentes e Alertas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Matrículas Recentes */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b flex justify-between items-center">
            <h3 className="font-semibold text-gray-900">Matrículas Recentes</h3>
            {hasPermission('matriculas.list') && (
              <Link 
                to="/matriculas" 
                className="text-sm text-indigo-600 hover:text-indigo-800"
              >
                Ver todas →
              </Link>
            )}
          </div>
          <div className="divide-y max-h-[400px] overflow-y-auto">
            {data.matriculas_recentes && data.matriculas_recentes.length > 0 ? (
              data.matriculas_recentes.map((matricula) => (
                <div key={matricula.id} className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900">{matricula.aluno_nome}</p>
                      <p className="text-sm text-gray-600">{matricula.curso_nome}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {matricula.extensao_nome} • {formatDate(matricula.dataMatricula)}
                      </p>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(matricula.status)}`}>
                      <FontAwesomeIcon icon={getStatusIcon(matricula.status)} />
                      {matricula.status}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500">
                Nenhuma matrícula recente
              </div>
            )}
          </div>
        </div>

        {/* Alunos que precisam de atenção */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b flex items-center gap-2">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-amber-500" />
            <h3 className="font-semibold text-gray-900">Alunos sem Matrícula Ativa</h3>
          </div>
          <div className="divide-y max-h-[400px] overflow-y-auto">
            {data.alunos_sem_matricula && data.alunos_sem_matricula.length > 0 ? (
              data.alunos_sem_matricula.map((aluno) => (
                <div key={aluno.idPessoa} className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900">{aluno.nome}</p>
                      <p className="text-sm text-gray-500">{aluno.extensao_nome}</p>
                      {aluno.email && (
                        <p className="text-xs text-gray-400 mt-1">
                          <FontAwesomeIcon icon={faEnvelope} className="mr-1" />
                          {aluno.email}
                        </p>
                      )}
                    </div>
                    <Link
                      to={`/alunos/${aluno.idPessoa}/edit`}
                      className="text-sm text-indigo-600 hover:text-indigo-800"
                    >
                      Ver aluno →
                    </Link>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500">
                <FontAwesomeIcon icon={faCheckCircle} className="text-3xl text-green-500 mb-2" />
                <p>Todos os alunos possuem matrícula ativa!</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pedidos Recentes */}
      {data.pedidos_recentes && data.pedidos_recentes.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b flex justify-between items-center">
            <h3 className="font-semibold text-gray-900">
              <FontAwesomeIcon icon={faShoppingCart} className="mr-2 text-blue-600" />
              Pedidos Recentes
            </h3>
            {hasPermission('pedidos.list') && (
              <Link 
                to="/pedidos" 
                className="text-sm text-indigo-600 hover:text-indigo-800"
              >
                Ver todos →
              </Link>
            )}
          </div>
          <div className="divide-y max-h-[400px] overflow-y-auto">
            {data.pedidos_recentes.map((pedido) => (
              <div key={pedido.id} className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-900">
                      Pedido #{pedido.codigo || pedido.id}
                    </p>
                    <p className="text-sm text-gray-600">{pedido.pessoa_nome || 'Cliente não identificado'}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {pedido.extensao_nome} • {formatDate(pedido.cadastradoEm)}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getPedidoStatusColor(pedido.status)}`}>
                      {pedido.status}
                    </span>
                    {pedido.valor && (
                      <p className="text-sm font-medium text-gray-900 mt-1">
                        R$ {parseFloat(pedido.valor).toFixed(2)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gráfico de Status de Matrículas */}
      {data.matriculas_por_status && data.matriculas_por_status.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Distribuição de Matrículas por Status</h3>
          <div className="flex flex-wrap gap-4">
            {data.matriculas_por_status.map((item) => (
              <div 
                key={item.status} 
                className={`flex items-center gap-2 px-4 py-2 rounded-lg ${getStatusColor(item.status)}`}
              >
                <FontAwesomeIcon icon={getStatusIcon(item.status)} />
                <span className="font-medium">{item.status}:</span>
                <span className="font-bold">{item.quantidade}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardCoordenador;
